import { AHText, AHTextProps } from "../Text/AHText";
import { AHTextFonts } from "../Text/AHTextFonts";
import { Box, TouchableBox } from "../AHTheme/Box";
import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Insets,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  ViewStyle,
} from "react-native";

import { AHNumberInputButton } from "./AHNumberInputButton";
import { BoxProps } from "../AHTheme/BoxProps";
import { formatToDisplay } from "./formatToDisplay";
import { useTheme } from "../AHTheme/AHTheme";

const filterNumericString = (str: string) => {
  let filteredString = "";
  const allowedChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "."];
  let hasDecimal = false;
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const negativeZero = i === 1 && char === "0" && str[i - 1] === "-";
    const doubleZero =
      i === 0 && char === "0" && str.length > 1 && str[i + 1] !== ".";
    if (allowedChars.includes(char) && !doubleZero && !negativeZero) {
      if (!(str[i] === "." && hasDecimal)) {
        filteredString = filteredString + str[i];
      }

      if (str[i] === ".") {
        hasDecimal = true;
      }
    }
  }
  return filteredString;
};

const styles = StyleSheet.create({
  textInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: Platform.select({ default: 50, web: 40 }),
    justifyContent: "center",
  },
  textInput: {
    fontFamily: AHTextFonts.REGULAR,
    fontSize: Platform.select({ default: 29, web: 18 }),
    width: Platform.select({ default: 135, web: 160 }),
    textAlign: "center",
  },
});

type AHNumberInputProps = {
  disabled: boolean;
  suffix?: string;
  setValue: (newValue: number) => void;
  value: number | undefined;
  inputAccessoryViewID?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  clearTextOnFocus: boolean;
  minValue: number;
  maxValue: number;
  buttonsShow?: boolean;
  placeholder?: string;
  leftElement?: React.ReactNode;
  inputContainerStyle?: StyleProp<ViewStyle>;
  adjustsFontSizeToFit?: boolean;
  buttonStyle?: StyleProp<ViewStyle>;
  hitSlop?: Insets;
};

function limitDecimals(text: string) {
  const a = text.split(".");
  return a[0] + (a[1] !== undefined ? "." + a[1].slice(0, 4) : "");
}

function parseFloatOrZero(p: string) {
  const parsed = parseFloat(p);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return parsed;
}

export type AHNumberInputRef = {
  focus: () => void;
};
export const AHNumberInput = ({
  value,
  setValue,
  disabled,
  suffix,
  clearTextOnFocus,
  minValue,
  maxValue,
  buttonsShow = true,
  onFocus,
  onBlur,
  autoFocus,
  inputContainerStyle,
  inputAccessoryViewID,
  adjustsFontSizeToFit = true,
  textInputStyle,
  tabIndex,
  placeholder,
  leftElement = null,
  buttonStyle,
  hitSlop,
  ref,
  ...boxProps
}: AHNumberInputProps &
  BoxProps & {
    textInputStyle?: AHTextProps["style"];
    tabIndex?: number;
    ref?: React.RefObject<AHNumberInputRef | null>;
  }) => {
  const theme = useTheme();
  const inputRef = useRef<TextInput>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [pendingComma, setPendingComma] = useState(false);
  const [textInputValue, setTextInputValue] = useState(
    value === undefined ? "" : value + ""
  );

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
        setIsFocused(true);
      }
    },
  }));

  const _onFocus = useCallback(() => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  const _onBlur = useCallback(() => {
    setIsFocused(false);

    onBlur?.();
  }, [onBlur]);

  const onDecrement = useCallback(() => {
    const numericValue = Math.max(
      parseFloatOrZero(textInputValue) - 1,
      minValue
    );
    setTextInputValue(numericValue + "");
    setValue(numericValue);
  }, [minValue, setValue, textInputValue]);

  const onIncrement = useCallback(() => {
    const numericValue = Math.min(
      parseFloatOrZero(textInputValue) + 1,
      maxValue
    );
    setTextInputValue(numericValue + "");
    setValue(numericValue);
  }, [maxValue, setValue, textInputValue]);

  const onChangeText = useCallback(
    (newTextValue: string) => {
      if (newTextValue === "") {
        setTextInputValue("");
        setValue(0);
        return;
      }

      const val = filterNumericString(newTextValue.replace(",", "."));
      const textValue = limitDecimals(val);
      const numericValue = parseFloat(textValue);
      if (
        !Number.isNaN(numericValue) &&
        numericValue <= maxValue &&
        numericValue >= minValue
      ) {
        setPendingComma(textValue.slice(-1) === ".");
        setTextInputValue(textValue);
        setValue(numericValue);
      }
    },
    [maxValue, minValue, setValue]
  );

  const valueString =
    (value === undefined
      ? ""
      : formatToDisplay(value) + (pendingComma ? "." : "")) +
    (suffix ? ` ${suffix}` : "");

  return (
    <Box
      spaceVertical="none"
      spaceHorizontal="none"
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
      }}
      {...boxProps}
    >
      {buttonsShow ? (
        <AHNumberInputButton
          disabled={disabled}
          onPress={onDecrement}
          type="decrement"
          style={buttonStyle}
        />
      ) : null}
      <TextInput
        style={{
          position: "absolute",
          bottom: 0,
          opacity: 0,
          width: 1,
          height: 1, // this is a hack to get it to work on android
        }}
        ref={inputRef}
        inputAccessoryViewID={inputAccessoryViewID}
        editable={!disabled}
        focusable
        selectTextOnFocus={Platform.OS === "android"} // when calling focus() on android, the selection of the text is not working so we have to select all of it
        value={textInputValue}
        keyboardType="numeric"
        onChangeText={onChangeText}
        showSoftInputOnFocus
        autoFocus={autoFocus}
        clearTextOnFocus={clearTextOnFocus}
        onFocus={_onFocus}
        onBlur={_onBlur}
      />
      <TouchableBox
        cornerRadius="medium"
        color="background"
        borderColor="border-primary"
        hitSlop={hitSlop ?? { top: 40, bottom: 40 }}
        tabIndex={-1}
        disabled={disabled}
        onPress={() => {
          if (!disabled) {
            inputRef.current?.focus();
          }
        }}
        style={[
          styles.textInputContainer,
          inputContainerStyle,
          {
            borderWidth: isFocused ? 2 : 1,
            borderColor: isFocused ? theme.primary : theme["border-primary"],
          },
        ]}
      >
        {leftElement}
        {value === undefined && placeholder ? (
          <AHText
            spaceLeft="xsmall"
            spaceRight="medium"
            numberOfLines={1}
            type="subtitle"
            color="text-washed"
            style={{ flexShrink: 1 }}
          >
            {placeholder}
          </AHText>
        ) : (
          <AHText
            style={[
              styles.textInput,
              {
                fontFamily: AHTextFonts.MONO,
                fontSize: Platform.select({
                  default: undefined,
                  web: valueString.length > 8 ? 14 : 22,
                }),
              },
              textInputStyle,
            ]}
            spaceHorizontal="medium"
            numberOfLines={1}
            adjustsFontSizeToFit={adjustsFontSizeToFit}
            minimumFontScale={0.3}
          >
            {valueString}
          </AHText>
        )}
      </TouchableBox>
      {buttonsShow ? (
        <AHNumberInputButton
          disabled={disabled}
          onPress={onIncrement}
          type="increment"
          style={buttonStyle}
        />
      ) : null}
    </Box>
  );
};
